import React, { useState, useEffect, useRef } from 'react'
import api from '../services/api.ts'
import { ADMIN } from '../services/endpoints.ts'
import { useAuth } from '../context/AuthContext.tsx'
import AccessDenied from '../components/AccessDenied.tsx'
import MessageBanner from '../components/MessageBanner.tsx'
import ConfirmModal from '../components/ConfirmModal.tsx'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'

interface LlmPrompt {
  id: string
  prompt_key: string
  name: string
  description: string | null
  prompt_text: string
  created_at: string
  updated_at: string
}

// Known template variables per prompt key. Unknown keys get no chips.
const TEMPLATE_VARIABLES_BY_KEY: Record<string, string[]> = {
  'call-analysis': [
    '{{transcript}}',
    '{{call_direction}}',
    '{{call_status}}',
    '{{call_duration_seconds}}',
    '{{contact_name}}',
    '{{is_first_time_caller}}',
  ],
  'monthly-sales-summary': [
    '{{company_name}}',
    '{{company_type}}',
    '{{window_description}}',
    '{{monthly_trend_data}}',
    '{{recent_sales_call_highlights}}',
    '{{average_membership_price}}',
    '{{customer_lifetime_value}}',
    '{{customer_lifetime_months}}',
  ],
}

/**
 * Resolve a prompt key to its base key so per-client variants like
 * "call-analysis-acme-dental" (the naming convention the Key placeholder
 * advertises) get the same variable chips and guardrails as the base prompt.
 */
function resolveBasePromptKey(key: string): string {
  const base = Object.keys(TEMPLATE_VARIABLES_BY_KEY).find(
    known => key === known || key.startsWith(`${known}-`),
  )
  return base || key
}

const EMPTY_FORM = { prompt_key: '', name: '', description: '', prompt_text: '' }

const AdminLlmPrompts: React.FC = () => {
  const { user } = useAuth()
  const [prompts, setPrompts] = useState<LlmPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<LlmPrompt | null>(null)
  const [creating, setCreating] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<LlmPrompt | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const fetchPrompts = async () => {
    setLoading(true)
    try {
      const res = await api.get(ADMIN.llmPrompts)
      setPrompts(res.data.prompts)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load prompts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPrompts()
  }, [])

  if (!user?.is_system_admin) {
    return <AccessDenied />
  }

  const startCreate = () => {
    setCreating(true)
    setEditing(null)
    setFormData(EMPTY_FORM)
    setMessage('')
    setError('')
  }

  const startEdit = (prompt: LlmPrompt) => {
    setEditing(prompt)
    setCreating(false)
    setFormData({
      prompt_key: prompt.prompt_key,
      name: prompt.name,
      description: prompt.description || '',
      prompt_text: prompt.prompt_text,
    })
    setMessage('')
    setError('')
  }

  const closeEditor = () => {
    setEditing(null)
    setCreating(false)
    setFormData(EMPTY_FORM)
  }

  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current
    if (!textarea) {
      setFormData(prev => ({ ...prev, prompt_text: prev.prompt_text + variable }))
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const next = formData.prompt_text.slice(0, start) + variable + formData.prompt_text.slice(end)
    setFormData(prev => ({ ...prev, prompt_text: next }))
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(start + variable.length, start + variable.length)
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage('')
    setError('')
    try {
      if (editing) {
        await api.put(ADMIN.llmPrompt(editing.id), {
          name: formData.name,
          description: formData.description,
          prompt_text: formData.prompt_text,
        })
        setMessage('Prompt updated')
      } else {
        await api.post(ADMIN.llmPrompts, formData)
        setMessage('Prompt created')
      }
      closeEditor()
      fetchPrompts()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save prompt')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setError('')
    try {
      await api.delete(ADMIN.llmPrompt(deleteTarget.id))
      setMessage(`Deleted "${deleteTarget.name}"`)
      setDeleteTarget(null)
      if (editing?.id === deleteTarget.id) closeEditor()
      fetchPrompts()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete prompt')
      setDeleteTarget(null)
    }
  }

  const showEditor = creating || editing !== null
  const basePromptKey = resolveBasePromptKey(formData.prompt_key)
  const templateVariables = TEMPLATE_VARIABLES_BY_KEY[basePromptKey] || []
  // The transcript guardrail applies to call-analysis prompts (including
  // per-client variants like call-analysis-acme-dental)
  const missingTranscript =
    showEditor &&
    basePromptKey === 'call-analysis' &&
    !formData.prompt_text.includes('{{transcript}}')

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">LLM Prompts</h1>
            <button
              onClick={startCreate}
              className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              New Prompt
            </button>
          </div>

          <MessageBanner message={message} error={error} />

          <TableContainer
            loading={loading}
            hasData={prompts.length > 0}
            emptyMessage="No prompts found"
          >
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <TableHeader
                  columns={[
                    { label: 'Key', key: 'prompt_key', sortable: false },
                    { label: 'Name', key: 'name', sortable: false },
                    { label: 'Description', key: 'description', sortable: false },
                    { label: 'Updated', key: 'updated_at', sortable: false },
                    { label: 'Actions', key: 'actions', sortable: false, align: 'right' },
                  ]}
                  sortBy=""
                  sortDir="asc"
                  onSort={() => {}}
                />
                <tbody className="bg-white divide-y divide-gray-200">
                  {prompts.map(prompt => (
                    <tr key={prompt.id} className={editing?.id === prompt.id ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 text-sm font-mono text-gray-700">
                        {prompt.prompt_key}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{prompt.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {prompt.description || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(prompt.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-right space-x-3">
                        <button
                          onClick={() => startEdit(prompt)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(prompt)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TableContainer>

          {showEditor && (
            <form
              onSubmit={handleSubmit}
              className="border border-gray-200 rounded-lg p-5 space-y-4"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                {editing ? `Edit "${editing.name}"` : 'New Prompt'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key {editing && <span className="text-gray-400 font-normal">(locked)</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.prompt_key}
                    onChange={e => setFormData(prev => ({ ...prev, prompt_key: e.target.value }))}
                    disabled={!!editing}
                    placeholder="call-analysis-acme-dental"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt text</label>
                {templateVariables.length > 0 && (
                  <>
                    <p className="text-xs text-gray-500 mb-1">
                      Template variables: click to insert
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {templateVariables.map(variable => (
                        <button
                          key={variable}
                          type="button"
                          onClick={() => insertVariable(variable)}
                          className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                        >
                          {variable}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <textarea
                  ref={textareaRef}
                  value={formData.prompt_text}
                  onChange={e => setFormData(prev => ({ ...prev, prompt_text: e.target.value }))}
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {missingTranscript && (
                  <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    This prompt does not include {'{{transcript}}'}, so the analyzer will have no
                    transcript content.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditor}
                  className="bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                >
                  {saving ? 'Saving...' : 'Save Prompt'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete prompt"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? The analysis service falls back to a bundled default if this prompt is removed.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

export default AdminLlmPrompts
